import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  ArrowLeft,
  FileText,
  Filter,
  History,
  Pencil,
  Trash2,
} from 'lucide-react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import VoidMembershipModal from './VoidMembershipModal';
import { useMembershipService } from 'services/memberships';
import { Membership as MembershipType } from 'types/membership';
import { ROUTES } from 'utils/constants';
import { toYYYYMMDD } from 'utils/dateUtils';
import {
  convertPDFToImages,
  createPrintContainer,
  createReportHeader,
} from 'pages/reports/pdfUtils';
import jsPDF from 'jspdf';
import {
  membershipPassesFieldFilters,
  type YesNoChoice,
} from 'utils/membershipListFilterUtils';
import styles from './Membership.module.scss';

type OptionalColumnId =
  | 'birthday'
  | 'email'
  | 'membershipDate'
  | 'gender'
  | 'civilStatus'
  | 'departmentMinistry'
  | 'nationality'
  | 'address'
  | 'contactNumber'
  | 'occupation'
  | 'employer'
  | 'membershipType'
  | 'isSgl'
  | 'isSgm'
  | 'willingToWorkInTupad'
  | 'emergencyContactName'
  | 'emergencyContactNumber'
  | 'notes';

interface ColumnDefinition {
  id: string;
  label: string;
  getValue: (membership: MembershipType) => string;
}

interface AddressModalData {
  name: string;
  address: string;
}

interface MembershipProps {
  showAddMembershipButton?: boolean;
  showActionButtons?: boolean;
  compactReadOnlyView?: boolean;
  showUtilityButtons?: boolean;
  showTitle?: boolean;
  /** Smaller heading (e.g. church report departmental embed). */
  compactTitle?: boolean;
  /** Slightly smaller than default page title (between default and compact). */
  mediumTitle?: boolean;
  /** Small heading variant used by report sub-pages. */
  smallTitle?: boolean;
  /** Tiny heading variant used for departmental member list titles. */
  tinyTitle?: boolean;
  titleText?: string;
  showBackButton?: boolean;
  /** Optional explicit route/path for back button navigation. */
  backButtonPath?: string;
  /** When true, the Show/Hide Columns panel is expanded on first render. */
  showColumnFiltersInitially?: boolean;
  /**
   * When set, only members with this `departmentMinistry` value are shown
   * (must match Add/Edit membership Department/Ministry options).
   */
  departmentMinistryFilter?: string;
  /** Called when refresh/reload overlay state changes (for external toolbars). */
  onReloadingChange?: (reloading: boolean) => void;
  /**
   * When true, left-aligns the report block (no centered max-width column).
   * Use for Church Reports embeds so the table aligns with the page edge.
   */
  alignContainerStart?: boolean;
  /** Gender / department / membership-type row filters (not shown when `compactReadOnlyView`). */
  showMembershipFieldFilters?: boolean;
}

export interface MembershipImperativeHandle {
  reload: () => void;
  downloadPdf: () => Promise<void>;
  downloadImage: () => Promise<void>;
}

function sanitizeExportFilenameBase(title: string): string {
  const raw = title
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return raw.slice(0, 80) || 'Member_List';
}

function addFooterToAllPages(pdf: jsPDF, reportTitle: string): void {
  const totalPages = pdf.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    const footerText = `${reportTitle} — Page ${page} of ${totalPages}`;
    const footerTextWidth = pdf.getTextWidth(footerText);
    pdf.text(footerText, pageWidth - 10 - footerTextWidth, pageHeight - 8);
  }
}

function getColumnCellStyle(
  columnId: string,
  compactReadOnlyView: boolean,
): React.CSSProperties | undefined {
  if (columnId === 'email' && compactReadOnlyView) {
    return {
      minWidth: '165px',
      width: '165px',
      maxWidth: '165px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  }
  if (columnId === 'birthday') {
    if (compactReadOnlyView) {
      return {
        minWidth: '90px',
        width: '90px',
        maxWidth: '90px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };
    }
    return {
      minWidth: '105px',
      width: '105px',
      maxWidth: '105px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  }
  if (columnId === 'address') {
    if (compactReadOnlyView) {
      return { minWidth: '110px', width: '110px', maxWidth: '110px' };
    }
    return {
      minWidth: '130px',
      width: '130px',
      maxWidth: '130px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  }
  if (columnId === 'contactNumber') {
    if (compactReadOnlyView) {
      return {
        minWidth: '110px',
        width: '110px',
        maxWidth: '110px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };
    }
    return {
      minWidth: '125px',
      width: '125px',
      maxWidth: '125px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  }
  return undefined;
}

function normalizeMembershipTypeLabel(value?: string | null): string {
  const raw = value?.trim();
  if (!raw) return '—';
  if (/^member\b/i.test(raw)) return 'Member';
  if (/^attender\b/i.test(raw)) return 'Attender';
  return raw;
}

function normalizeExportMembershipTypeLabel(value?: string | null): string {
  const base = normalizeMembershipTypeLabel(value);
  return base.replace(/\s*\(administration\)\s*/gi, '').trim();
}

function formatMembershipName(membership: MembershipType): string {
  const last = membership.lastName?.trim() ?? '';
  const first = membership.firstName?.trim() ?? '';
  const middle = membership.middleName?.trim() ?? '';
  const fullName = [last, first, middle].filter(Boolean).join(', ');
  return fullName || '—';
}

function normalizeSearchValue(value?: string | null): string {
  return (value ?? '').trim().toLowerCase();
}

function membershipMatchesQuery(
  membership: MembershipType,
  normalizedQuery: string,
): boolean {
  if (!normalizedQuery) return true;

  const tokens = [
    membership.firstName,
    membership.middleName,
    membership.lastName,
    `${membership.firstName ?? ''} ${membership.middleName ?? ''} ${
      membership.lastName ?? ''
    }`,
    `${membership.lastName ?? ''}, ${membership.firstName ?? ''} ${
      membership.middleName ?? ''
    }`,
    membership.email,
    membership.contactNumber,
    membership.address,
    membership.gender,
    membership.civilStatus,
    membership.departmentMinistry,
    membership.nationality,
    membership.occupation,
    membership.employer,
    membership.emergencyContactName,
    membership.emergencyContactNumber,
    membership.notes,
    normalizeMembershipTypeLabel(membership.membershipType),
    toYYYYMMDD(membership.dateOfBirth ?? undefined) ?? '',
    toYYYYMMDD(membership.membershipDate) ?? '',
  ];

  return tokens.some((token) =>
    normalizeSearchValue(token).includes(normalizedQuery),
  );
}

type SortDirection = 'asc' | 'desc';

const OPTIONAL_COLUMNS: Array<{ id: OptionalColumnId; label: string }> = [
  { id: 'address', label: 'Address' },
  { id: 'birthday', label: 'Birthday' },
  { id: 'civilStatus', label: 'Civil Status' },
  { id: 'contactNumber', label: 'Contact number' },
  { id: 'departmentMinistry', label: 'Department / Ministry' },
  { id: 'email', label: 'Email address' },
  { id: 'emergencyContactName', label: 'Emergency Contact Name' },
  { id: 'emergencyContactNumber', label: 'Emergency Contact number' },
  { id: 'employer', label: 'Employer / Company Name' },
  { id: 'gender', label: 'Gender' },
  { id: 'isSgl', label: 'SGL (Small Group Leader)' },
  { id: 'isSgm', label: 'SGM (Small Group Member)' },
  { id: 'membershipDate', label: 'Membership date' },
  { id: 'membershipType', label: 'Membership Type' },
  { id: 'nationality', label: 'Nationality' },
  { id: 'notes', label: 'Notes' },
  { id: 'occupation', label: 'Occupation' },
  { id: 'willingToWorkInTupad', label: 'Willing to work in TUPAD' },
];

function buildColumnVisibility(
  value: boolean,
): Record<OptionalColumnId, boolean> {
  return OPTIONAL_COLUMNS.reduce<Record<OptionalColumnId, boolean>>(
    (accumulator, column) => {
      accumulator[column.id] = value;
      return accumulator;
    },
    {} as Record<OptionalColumnId, boolean>,
  );
}

/** Default optional columns shown on first load. */
function buildDefaultColumnVisibility(): Record<OptionalColumnId, boolean> {
  const visible: OptionalColumnId[] = ['birthday', 'gender', 'membershipType'];
  return OPTIONAL_COLUMNS.reduce<Record<OptionalColumnId, boolean>>(
    (accumulator, column) => {
      accumulator[column.id] = visible.includes(column.id);
      return accumulator;
    },
    {} as Record<OptionalColumnId, boolean>,
  );
}

function buildInitialColumnVisibility(
  departmentMinistryFilter?: string,
): Record<OptionalColumnId, boolean> {
  const base = buildDefaultColumnVisibility();
  if (departmentMinistryFilter?.trim()) {
    return { ...base, departmentMinistry: true };
  }
  return base;
}

const Membership = forwardRef<MembershipImperativeHandle, MembershipProps>(
  function Membership(
    {
      showAddMembershipButton = true,
      showActionButtons = true,
      compactReadOnlyView = false,
      showUtilityButtons = true,
      showTitle = true,
      compactTitle = false,
      mediumTitle = false,
      smallTitle = false,
      tinyTitle = false,
      titleText = "General Member's List",
      showBackButton = false,
      backButtonPath,
      showColumnFiltersInitially = false,
      departmentMinistryFilter,
      onReloadingChange,
      alignContainerStart = false,
      showMembershipFieldFilters = true,
    },
    ref,
  ) {
    const navigate = useNavigate();
    const location = useLocation();
    const membershipService = useMembershipService();
    const [memberships, setMemberships] = useState<MembershipType[]>([]);
    const [loading, setLoading] = useState(true);
    const [reloading, setReloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<{
      message: string;
      type: 'success' | 'error';
    } | null>(null);
    const [membershipToVoid, setMembershipToVoid] =
      useState<MembershipType | null>(null);
    const [addressModalData, setAddressModalData] =
      useState<AddressModalData | null>(null);
    const [columnVisibility, setColumnVisibility] = useState<
      Record<OptionalColumnId, boolean>
    >(() => buildInitialColumnVisibility(departmentMinistryFilter));
    const [showColumnFilters, setShowColumnFilters] = useState(
      showColumnFiltersInitially,
    );
    const [sortColumnId, setSortColumnId] = useState<string>('surname');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [searchInputValue, setSearchInputValue] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [civilStatusFilter] = useState<string[]>([]);
    const [occupationFilter] = useState<string[]>([]);
    const [nationalityFilter] = useState<string[]>([]);
    const [isSglFilter, setIsSglFilter] = useState<YesNoChoice[]>([]);
    const [isSgmFilter, setIsSgmFilter] = useState<YesNoChoice[]>([]);
    const [willingFilter, setWillingFilter] = useState<YesNoChoice[]>([]);
    const [showMembershipFilters, setShowMembershipFilters] = useState(false);
    const [tablePage, setTablePage] = useState(1);

    const loadData = useCallback(
      async (isReload = false) => {
        if (isReload) setReloading(true);
        else setLoading(true);
        setError(null);
        try {
          const data = await membershipService.listMemberships(false);
          setMemberships(data);
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to load memberships.';
          setError(message);
        } finally {
          if (isReload) setReloading(false);
          else setLoading(false);
        }
      },
      [membershipService],
    );

    useEffect(() => {
      void loadData();
    }, [loadData]);

    useEffect(() => {
      onReloadingChange?.(reloading);
    }, [reloading, onReloadingChange]);

    useEffect(() => {
      const state = location.state as {
        toast?: { message: string; type: 'success' | 'error' };
      } | null;
      if (state?.toast) {
        setToast(state.toast);
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: {} },
        );
      }
    }, [location.pathname, location.search, location.state, navigate]);

    const showToast = (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    };

    const visibleMemberships = useMemo(() => {
      const active = memberships.filter(
        (membership) => membership.isVoided !== true,
      );
      const dept = departmentMinistryFilter?.trim();
      if (!dept) return active;
      return active.filter(
        (membership) => (membership.departmentMinistry?.trim() ?? '') === dept,
      );
    }, [memberships, departmentMinistryFilter]);

    const showFieldFilterRow =
      showMembershipFieldFilters && !compactReadOnlyView;

    const listAfterFieldFilters = useMemo(() => {
      if (!showFieldFilterRow) return visibleMemberships;
      return visibleMemberships.filter((m) =>
        membershipPassesFieldFilters(m, {
          civilStatus: civilStatusFilter,
          occupation: occupationFilter,
          nationality: nationalityFilter,
          isSgl: isSglFilter,
          isSgm: isSgmFilter,
          willingToWorkInTupad: willingFilter,
          membershipType: [],
        }),
      );
    }, [
      visibleMemberships,
      showFieldFilterRow,
      civilStatusFilter,
      occupationFilter,
      nationalityFilter,
      isSglFilter,
      isSgmFilter,
      willingFilter,
    ]);

    const normalizedAppliedSearchQuery = useMemo(
      () => normalizeSearchValue(appliedSearchQuery),
      [appliedSearchQuery],
    );

    const normalizedInputSearchQuery = useMemo(
      () => normalizeSearchValue(searchInputValue),
      [searchInputValue],
    );

    const filteredMemberships = useMemo(
      () =>
        listAfterFieldFilters.filter((membership) =>
          membershipMatchesQuery(membership, normalizedAppliedSearchQuery),
        ),
      [normalizedAppliedSearchQuery, listAfterFieldFilters],
    );

    const suggestedMemberships = useMemo(
      () =>
        normalizedInputSearchQuery
          ? listAfterFieldFilters
              .filter((membership) =>
                membershipMatchesQuery(membership, normalizedInputSearchQuery),
              )
              .slice(0, 6)
          : [],
      [normalizedInputSearchQuery, listAfterFieldFilters],
    );

    const fixedColumns = useMemo<ColumnDefinition[]>(
      () => [
        {
          id: 'surname',
          label: 'Surname',
          getValue: (membership) => membership.lastName?.trim() || '—',
        },
        {
          id: 'firstName',
          label: 'First Name',
          getValue: (membership) => membership.firstName?.trim() || '—',
        },
        {
          id: 'middleName',
          label: 'Middle Name',
          getValue: (membership) => membership.middleName?.trim() || '—',
        },
      ],
      [],
    );

    const optionalColumns = useMemo<ColumnDefinition[]>(
      () => [
        {
          id: 'birthday',
          label: 'Birthday',
          getValue: (membership) =>
            toYYYYMMDD(membership.dateOfBirth ?? undefined) ?? '—',
        },
        {
          id: 'email',
          label: 'Email address',
          getValue: (membership) => membership.email?.trim() || '—',
        },
        {
          id: 'membershipDate',
          label: 'Membership date',
          getValue: (membership) =>
            toYYYYMMDD(membership.membershipDate) ?? '—',
        },
        {
          id: 'gender',
          label: 'Gender',
          getValue: (membership) => membership.gender?.trim() || '—',
        },
        {
          id: 'civilStatus',
          label: 'Civil Status',
          getValue: (membership) => membership.civilStatus?.trim() || '—',
        },
        {
          id: 'departmentMinistry',
          label: 'Department / Ministry',
          getValue: (membership) =>
            membership.departmentMinistry?.trim() || '—',
        },
        {
          id: 'nationality',
          label: 'Nationality',
          getValue: (membership) => membership.nationality?.trim() || '—',
        },
        {
          id: 'address',
          label: 'Address',
          getValue: (membership) => membership.address?.trim() || '—',
        },
        {
          id: 'contactNumber',
          label: 'Contact number',
          getValue: (membership) => membership.contactNumber?.trim() || '—',
        },
        {
          id: 'occupation',
          label: 'Occupation',
          getValue: (membership) => membership.occupation?.trim() || '—',
        },
        {
          id: 'employer',
          label: 'Employer / Company Name',
          getValue: (membership) => membership.employer?.trim() || '—',
        },
        {
          id: 'membershipType',
          label: 'Membership Type',
          getValue: (membership) =>
            normalizeMembershipTypeLabel(membership.membershipType),
        },
        {
          id: 'isSgl',
          label: 'SGL (Small Group Leader)',
          getValue: (membership) => (membership.isSgl ? 'Yes' : 'No'),
        },
        {
          id: 'isSgm',
          label: 'SGM (Small Group Member)',
          getValue: (membership) => (membership.isSgm ? 'Yes' : 'No'),
        },
        {
          id: 'willingToWorkInTupad',
          label: 'Willing to work in TUPAD',
          getValue: (membership) =>
            membership.willingToWorkInTupad ? 'Yes' : 'No',
        },
        {
          id: 'emergencyContactName',
          label: 'Emergency Contact Name',
          getValue: (membership) =>
            membership.emergencyContactName?.trim() || '—',
        },
        {
          id: 'emergencyContactNumber',
          label: 'Emergency Contact number',
          getValue: (membership) =>
            membership.emergencyContactNumber?.trim() || '—',
        },
        {
          id: 'notes',
          label: 'Notes',
          getValue: (membership) => membership.notes?.trim() || '—',
        },
      ],
      [],
    );

    const selectedOptionalColumns = useMemo(
      () =>
        optionalColumns.filter(
          (column) => columnVisibility[column.id as OptionalColumnId],
        ),
      [columnVisibility, optionalColumns],
    );

    const visibleColumns = useMemo(
      () => [...fixedColumns, ...selectedOptionalColumns],
      [fixedColumns, selectedOptionalColumns],
    );

    const sortedMemberships = useMemo(() => {
      const getFallbackNameCompare = (
        a: MembershipType,
        b: MembershipType,
      ): number => {
        const surnameCompare = (a.lastName ?? '')
          .trim()
          .localeCompare((b.lastName ?? '').trim(), undefined, {
            sensitivity: 'base',
          });
        if (surnameCompare !== 0) return surnameCompare;
        return (a.firstName ?? '')
          .trim()
          .localeCompare((b.firstName ?? '').trim(), undefined, {
            sensitivity: 'base',
          });
      };

      const targetColumn =
        visibleColumns.find((column) => column.id === sortColumnId) ??
        fixedColumns[0];

      const sorted = [...filteredMemberships].sort((a, b) => {
        if (targetColumn.id === 'birthday') {
          const aDate = toYYYYMMDD(a.dateOfBirth ?? undefined);
          const bDate = toYYYYMMDD(b.dateOfBirth ?? undefined);
          const aYear = aDate ? Number(aDate.slice(0, 4)) : Number.NaN;
          const bYear = bDate ? Number(bDate.slice(0, 4)) : Number.NaN;

          if (Number.isFinite(aYear) && Number.isFinite(bYear)) {
            const yearCompare = aYear - bYear;
            if (yearCompare !== 0) return yearCompare;
          } else if (Number.isFinite(aYear)) {
            return -1;
          } else if (Number.isFinite(bYear)) {
            return 1;
          }
        }

        const primaryCompare = targetColumn
          .getValue(a)
          .localeCompare(targetColumn.getValue(b), undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        if (primaryCompare !== 0) return primaryCompare;

        return getFallbackNameCompare(a, b);
      });

      return sortDirection === 'desc' ? sorted.reverse() : sorted;
    }, [
      fixedColumns,
      filteredMemberships,
      sortColumnId,
      sortDirection,
      visibleColumns,
    ]);

    const tablePageSize = 20;
    const tableTotalPages = Math.max(
      1,
      Math.ceil(sortedMemberships.length / tablePageSize),
    );

    useEffect(() => {
      setTablePage((previous) => Math.min(previous, tableTotalPages));
    }, [tableTotalPages]);

    const paginatedMemberships = useMemo(() => {
      const start = (tablePage - 1) * tablePageSize;
      return sortedMemberships.slice(start, start + tablePageSize);
    }, [sortedMemberships, tablePage]);

    const toggleColumn = useCallback((columnId: OptionalColumnId) => {
      setColumnVisibility((previous) => ({
        ...previous,
        [columnId]: !previous[columnId],
      }));
    }, []);

    const handleHeaderSortClick = useCallback(
      (columnId: string) => {
        setSortDirection((previousDirection) =>
          sortColumnId === columnId
            ? previousDirection === 'asc'
              ? 'desc'
              : 'asc'
            : 'asc',
        );
        setSortColumnId(columnId);
      },
      [sortColumnId],
    );

    const applySearch = useCallback(() => {
      setAppliedSearchQuery(searchInputValue);
    }, [searchInputValue]);

    const toggleYesNoValue = useCallback(
      (
        value: YesNoChoice,
        setter: React.Dispatch<React.SetStateAction<YesNoChoice[]>>,
      ) => {
        setter((previous) =>
          previous.includes(value)
            ? previous.filter((v) => v !== value)
            : [...previous, value],
        );
      },
      [],
    );

    const buildExportContainer = useCallback(
      (reportTitle: string): HTMLElement => {
        const printContainer = createPrintContainer();
        // Add a small inner margin for exported PDF/image output.
        printContainer.style.padding = '6mm';
        printContainer.style.boxSizing = 'border-box';
        const generatedOn = new Date().toLocaleString();

        printContainer.appendChild(
          createReportHeader(reportTitle, `Generated: ${generatedOn}`),
        );

        const table = document.createElement('table');
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.tableLayout = 'fixed';
        table.style.fontSize = '12px';

        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        visibleColumns.forEach((column) => {
          const th = document.createElement('th');
          th.textContent = column.label;
          th.style.padding = '8px 6px';
          th.style.textAlign = 'left';
          th.style.fontWeight = '700';
          th.style.background = 'hsl(120 45% 94%)';
          th.style.color = 'hsl(142 45% 30%)';
          th.style.border = 'none';
          th.style.wordBreak = 'break-word';
          if (column.id === 'address') {
            th.style.minWidth = '260px';
          }
          headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        const tbody = document.createElement('tbody');
        sortedMemberships.forEach((membership, rowIndex) => {
          const tr = document.createElement('tr');
          if (rowIndex % 2 === 1) {
            tr.style.background = '#fbfbfb';
          }
          visibleColumns.forEach((column) => {
            const td = document.createElement('td');
            const rawValue = column.getValue(membership);
            td.textContent =
              column.id === 'membershipType'
                ? normalizeExportMembershipTypeLabel(rawValue)
                : rawValue;
            td.style.padding = '8px 6px';
            td.style.border = 'none';
            td.style.verticalAlign = 'top';
            td.style.wordBreak = 'break-word';
            if (column.id === 'address') {
              td.style.minWidth = '260px';
            }
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        printContainer.appendChild(table);
        return printContainer;
      },
      [sortedMemberships, visibleColumns],
    );

    const handleDownloadPdf = useCallback((): Promise<void> => {
      try {
        const reportTitle = titleText.trim() || 'Member List';
        const dateSuffix = toYYYYMMDD(new Date().toISOString()) ?? 'today';
        const fileBase = sanitizeExportFilenameBase(reportTitle);

        const orientation =
          visibleColumns.length > 7 ||
          visibleColumns.some((c) => c.id === 'address')
            ? 'l'
            : 'p';
        const pdf = new jsPDF({
          orientation,
          unit: 'mm',
          format: 'a4',
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const marginX = 10;
        const headerTop = 12;
        const footerReserve = 14;
        let y = headerTop;

        // Header block (same polished look used across report PDFs)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        const titleWidth = pdf.getTextWidth(reportTitle);
        pdf.text(reportTitle, (pageWidth - titleWidth) / 2, y);
        y += 7;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        const generatedOn = `Generated: ${new Date().toLocaleString()}`;
        const generatedOnWidth = pdf.getTextWidth(generatedOn);
        pdf.text(generatedOn, (pageWidth - generatedOnWidth) / 2, y);
        y += 8;

        const tableStartY = y;
        const usableWidth = pageWidth - marginX * 2;
        const usableHeight = pageHeight - footerReserve - tableStartY;
        const bodyBaseFontSize = visibleColumns.length > 14 ? 5.8 : 6.4;
        const headerFontSize = visibleColumns.length > 14 ? 6.2 : 6.8;

        // Weighted widths tuned for PDF readability.
        // Keep most columns single-line, while allowing Address to wrap.
        const weightForColumn = (columnId: string): number => {
          if (columnId === 'surname' || columnId === 'firstName') return 1.6;
          if (columnId === 'middleName') return 1.3;
          if (columnId === 'birthday') return 1.45;
          if (columnId === 'membershipDate') return 1.7;
          if (columnId === 'email') return 3.0;
          if (columnId === 'gender') return 1.25;
          if (columnId === 'civilStatus') return 1.1;
          if (columnId === 'departmentMinistry') return 2.2;
          if (columnId === 'nationality') return 1.2;
          if (columnId === 'address') return 4.3;
          if (columnId === 'contactNumber') return 1.65;
          if (columnId === 'occupation') return 1.55;
          if (columnId === 'employer') return 1.75;
          if (columnId === 'membershipType') return 1.5;
          if (columnId === 'isSgl' || columnId === 'isSgm') return 1.0;
          if (columnId === 'willingToWorkInTupad') return 1.35;
          if (columnId === 'emergencyContactName') return 1.7;
          if (columnId === 'emergencyContactNumber') return 1.5;
          if (columnId === 'notes') return 1.2;
          return 1;
        };

        const totalWeight = visibleColumns.reduce(
          (sum, column) => sum + weightForColumn(column.id),
          0,
        );
        const columnWidths = visibleColumns.map(
          (column) => (usableWidth * weightForColumn(column.id)) / totalWeight,
        );
        const columnX = columnWidths.reduce<number[]>(
          (acc, width, index) => [
            ...acc,
            index === 0 ? marginX : acc[index - 1] + columnWidths[index - 1],
          ],
          [],
        );

        const drawHeader = (headerY: number) => {
          pdf.setFillColor(232, 246, 235);
          pdf.rect(marginX, headerY, usableWidth, 8, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(headerFontSize);
          pdf.setTextColor(26, 83, 60);
          const getPdfHeaderLabel = (
            columnId: string,
            label: string,
          ): string => {
            if (columnId === 'firstName') return 'First';
            if (columnId === 'middleName') return 'Middle';
            if (columnId === 'membershipDate') return 'Mem Date';
            if (columnId === 'departmentMinistry') return 'Department';
            if (columnId === 'contactNumber') return 'Contact';
            if (columnId === 'membershipType') return 'Memb Type';
            if (columnId === 'willingToWorkInTupad') return 'Willing';
            if (columnId === 'emergencyContactName') return 'Emer Name';
            if (columnId === 'emergencyContactNumber') return 'Emer #';
            return label;
          };
          const getCellLeftPadding = (columnId: string): number => {
            if (columnId === 'email') return 2.2;
            if (columnId === 'gender') return 1.8;
            return 1.2;
          };
          visibleColumns.forEach((column, index) => {
            const leftPadding = getCellLeftPadding(column.id);
            const maxTextWidth = Math.max(
              4,
              columnWidths[index] - leftPadding - 1.3,
            );
            const splitHeaderUnknown: unknown = pdf.splitTextToSize(
              getPdfHeaderLabel(column.id, column.label),
              maxTextWidth,
            );
            const headerLines = Array.isArray(splitHeaderUnknown)
              ? splitHeaderUnknown.map((line) => String(line))
              : [String(splitHeaderUnknown)];
            const headerText = headerLines[0] ?? '';
            pdf.text(headerText, columnX[index] + leftPadding, headerY + 5.2);
          });
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(bodyBaseFontSize);
        };

        const formatPdfCellValue = (
          columnId: string,
          rawValue: string,
        ): string => {
          const value = (rawValue ?? '').trim() || '—';

          if (columnId === 'membershipType') {
            return normalizeExportMembershipTypeLabel(value);
          }

          if (columnId === 'nationality') {
            if (/^philippines?$/i.test(value)) return 'PHIL';
            return value.toUpperCase();
          }

          if (columnId === 'contactNumber') {
            const compact = value.replace(/\s+/g, '');
            const intlMatch = compact.match(/^(\+63)(\d{3})(\d{3})(\d{4})$/);
            if (intlMatch) {
              return `${intlMatch[1]} ${intlMatch[2]} ${intlMatch[3]} ${intlMatch[4]}`;
            }
            const localMatch = compact.match(/^(0\d{3})(\d{3})(\d{4})$/);
            if (localMatch) {
              return `${localMatch[1]} ${localMatch[2]} ${localMatch[3]}`;
            }
            return value;
          }

          if (columnId === 'occupation') {
            return value.replace(/([/-])/g, ' $1 ').replace(/\s{2,}/g, ' ');
          }

          return value;
        };

        const splitPdfCellLines = (
          columnId: string,
          rawValue: string,
          maxWidth: number,
        ): string[] => {
          const formatted = formatPdfCellValue(columnId, rawValue);
          if (!formatted) return ['—'];

          if (columnId === 'email' && formatted.includes('@')) {
            const [localPart, domainPart] = formatted.split('@');
            if (localPart && domainPart) {
              const localTokens = localPart
                .split(/([._-])/)
                .filter((token) => token.length > 0);
              const localLines: string[] = [];
              let current = '';
              localTokens.forEach((token) => {
                const candidate = `${current}${token}`;
                if (!current || pdf.getTextWidth(candidate) <= maxWidth) {
                  current = candidate;
                  return;
                }
                localLines.push(current);
                current = token;
              });
              if (current) localLines.push(current);
              return [...localLines, `@${domainPart}`];
            }
          }

          const words = formatted.split(/\s+/).filter(Boolean);
          if (words.length === 0) return ['—'];

          const lines: string[] = [];
          let currentLine = '';

          words.forEach((word) => {
            const candidate = currentLine ? `${currentLine} ${word}` : word;
            const candidateWidth = pdf.getTextWidth(candidate);
            if (!currentLine || candidateWidth <= maxWidth) {
              currentLine = candidate;
              return;
            }
            lines.push(currentLine);
            // Keep long single words intact (no forced character cutting).
            currentLine = word;
          });

          if (currentLine) lines.push(currentLine);
          return lines;
        };

        const drawRow = (
          rowY: number,
          membership: MembershipType,
          rowIndex: number,
        ): number => {
          const cellLinesByColumn = visibleColumns.map((column, index) =>
            splitPdfCellLines(
              column.id,
              column.getValue(membership),
              Math.max(6, columnWidths[index] - 2.5),
            ),
          );
          const maxLines = Math.max(
            1,
            ...cellLinesByColumn.map((cellLines) => cellLines.length),
          );
          const rowHeight = Math.max(6, maxLines * 3.9 + 1.6);

          if (rowIndex % 2 === 1) {
            pdf.setFillColor(251, 251, 251);
            pdf.rect(marginX, rowY, usableWidth, rowHeight, 'F');
          }

          const getCellLeftPadding = (columnId: string): number => {
            if (columnId === 'email') return 2.2;
            if (columnId === 'gender') return 1.8;
            return 1.2;
          };

          visibleColumns.forEach((column, index) => {
            const lines = cellLinesByColumn[index];
            const leftPadding = getCellLeftPadding(column.id);
            pdf.setFontSize(bodyBaseFontSize);
            pdf.text(lines, columnX[index] + leftPadding, rowY + 4);
          });
          pdf.setFontSize(bodyBaseFontSize);

          pdf.setDrawColor(235, 235, 235);
          pdf.line(
            marginX,
            rowY + rowHeight,
            marginX + usableWidth,
            rowY + rowHeight,
          );
          return rowHeight;
        };

        let currentY = tableStartY;
        drawHeader(currentY);
        currentY += 8;

        sortedMemberships.forEach((membership, rowIndex) => {
          const cellLinesByColumn = visibleColumns.map((column, index) =>
            splitPdfCellLines(
              column.id,
              column.getValue(membership),
              Math.max(6, columnWidths[index] - 2.5),
            ),
          );
          const maxLines = Math.max(
            1,
            ...cellLinesByColumn.map((cellLines) => cellLines.length),
          );
          const projectedRowHeight = Math.max(6, maxLines * 3.9 + 1.6);

          if (currentY - tableStartY + projectedRowHeight > usableHeight) {
            pdf.addPage();
            currentY = headerTop;
            drawHeader(currentY);
            currentY += 8;
          }

          const rowHeight = drawRow(currentY, membership, rowIndex);
          currentY += rowHeight;
        });

        addFooterToAllPages(pdf, reportTitle);
        pdf.save(`${fileBase}_${dateSuffix}.pdf`);
        showToast('PDF downloaded successfully.', 'success');
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to download PDF. Please try again.';
        showToast(message, 'error');
      }
      return Promise.resolve();
    }, [sortedMemberships, titleText, visibleColumns]);

    const handleDownloadImage = useCallback(async () => {
      try {
        const reportTitle = titleText.trim() || 'Member List';
        const printContainer = buildExportContainer(reportTitle);
        const dateSuffix = toYYYYMMDD(new Date().toISOString()) ?? 'today';
        const fileBase = sanitizeExportFilenameBase(reportTitle);
        await convertPDFToImages(
          printContainer,
          `${fileBase}_${dateSuffix}.pdf`,
          reportTitle,
        );
        showToast('Image file downloaded successfully.', 'success');
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to download image file. Please try again.';
        showToast(message, 'error');
      }
    }, [buildExportContainer, titleText]);

    useImperativeHandle(
      ref,
      () => ({
        reload: () => {
          void loadData(true);
        },
        downloadPdf: () => handleDownloadPdf(),
        downloadImage: () => handleDownloadImage(),
      }),
      [loadData, handleDownloadPdf, handleDownloadImage],
    );

    const navigateToEdit = useCallback(
      (membershipId: string) => {
        navigate(ROUTES.MEMBERSHIP.EDIT.replace(':id', membershipId));
      },
      [navigate],
    );

    const navigateToHistory = useCallback(
      (membershipId: string) => {
        navigate(ROUTES.MEMBERSHIP.VIEW_HISTORY.replace(':id', membershipId));
      },
      [navigate],
    );

    const handleVoidSuccess = useCallback(
      (voidedMembership: MembershipType) => {
        setMemberships((previous) =>
          previous.map((membership) =>
            membership.id === voidedMembership.id
              ? voidedMembership
              : membership,
          ),
        );
        showToast('Membership deleted successfully.', 'success');
      },
      [],
    );

    const renderCellValue = useCallback(
      (
        membership: MembershipType,
        column: ColumnDefinition,
      ): React.ReactNode => {
        const value = column.getValue(membership);
        if (column.id === 'address' && value !== '—') {
          return (
            <button
              type="button"
              className={styles.addressLinkButton}
              onClick={() =>
                setAddressModalData({
                  name: formatMembershipName(membership),
                  address: value,
                })
              }
              title={value}
            >
              View address
            </button>
          );
        }
        return value;
      },
      [],
    );

    return (
      <div
        className={`${styles.container} ${
          compactReadOnlyView ? styles.compactReadOnlyView : ''
        } ${alignContainerStart ? styles.containerAlignStart : ''}`}
      >
        <header className={styles.header}>
          <div
            className={`${styles.headerLeft} ${
              showBackButton && showTitle ? styles.headerLeftStacked : ''
            }`}
          >
            {showBackButton && (
              <Button
                variant="ghost"
                onClick={() =>
                  backButtonPath ? navigate(backButtonPath) : navigate(-1)
                }
                aria-label="Go back"
              >
                <ArrowLeft size={16} />
                Back
              </Button>
            )}
            {showTitle && (
              <h1
                className={`${styles.title}${
                  compactTitle
                    ? ` ${styles.titleCompact}`
                    : mediumTitle
                      ? ` ${styles.titleMedium}`
                      : smallTitle
                        ? ` ${styles.titleSmall}`
                        : tinyTitle
                          ? ` ${styles.titleTiny}`
                          : ''
                }`}
              >
                {titleText}
              </h1>
            )}
          </div>
          <div className={styles.headerButtons}>
            {showAddMembershipButton && (
              <>
                <Button onClick={() => navigate(ROUTES.MEMBERSHIP.ADD)}>
                  Add Membership
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void handleDownloadPdf()}
                  aria-label="Download membership list PDF"
                  title="Download PDF"
                >
                  <FileText size={14} />
                  Download PDF
                </Button>
              </>
            )}
            {showUtilityButtons && (
              <>
                <RefreshButton
                  onClick={() => void loadData(true)}
                  loading={reloading}
                />
                <Button
                  variant="outline"
                  onClick={() => void handleDownloadPdf()}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void handleDownloadImage()}
                >
                  Download Image
                </Button>
              </>
            )}
          </div>
        </header>

        <div className={styles.columnFilterControls}>
          <div className={styles.searchInputWrap}>
            <input
              type="search"
              value={searchInputValue}
              onChange={(event) => {
                const nextValue = event.target.value;
                setSearchInputValue(nextValue);
                if (!normalizeSearchValue(nextValue)) {
                  setAppliedSearchQuery('');
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applySearch();
                }
              }}
              className={styles.searchInput}
              placeholder="Search member details..."
              aria-label="Search members"
            />
            {normalizedInputSearchQuery && suggestedMemberships.length > 0 && (
              <ul className={styles.searchSuggestions} aria-label="Suggestions">
                {suggestedMemberships.map((membership) => (
                  <li key={membership.id}>
                    <button
                      type="button"
                      className={styles.searchSuggestionButton}
                      onClick={() => {
                        const fullName = formatMembershipName(membership);
                        setSearchInputValue(fullName);
                        setAppliedSearchQuery(fullName);
                      }}
                    >
                      <span className={styles.searchSuggestionName}>
                        {formatMembershipName(membership)}
                      </span>
                      <span className={styles.searchSuggestionMeta}>
                        {membership.email?.trim() || 'No email'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className={styles.searchMeta}>
              Showing {filteredMemberships.length} of{' '}
              {listAfterFieldFilters.length} members
            </p>
            {normalizedInputSearchQuery !== normalizedAppliedSearchQuery && (
              <p className={styles.searchHint}>Press Enter to apply search.</p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowMembershipFilters((prev) => !prev)}
          >
            <Filter size={14} />
            {showMembershipFilters ? 'Hide Filters' : 'Filter'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowColumnFilters((previous) => !previous)}
          >
            {showColumnFilters ? 'Hide Columns' : 'Show Columns'}
          </Button>
          {showColumnFilters && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setColumnVisibility(buildColumnVisibility(true))}
              >
                Select all
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setColumnVisibility(buildColumnVisibility(false))
                }
              >
                Deselect all
              </Button>
            </>
          )}
        </div>

        {showFieldFilterRow && showMembershipFilters && (
          <div
            className={styles.membershipFiltersRow}
            aria-label="Filter members by field"
          >
            <div className={styles.filterGroup}>
              <div className={styles.filterGroupTitle}>
                SGL (Small Group Leader)
              </div>
              <label className={styles.filterCheckLabel}>
                <input
                  type="checkbox"
                  checked={isSglFilter.includes('yes')}
                  onChange={() => toggleYesNoValue('yes', setIsSglFilter)}
                />
                Yes
              </label>
              <label className={styles.filterCheckLabel}>
                <input
                  type="checkbox"
                  checked={isSglFilter.includes('no')}
                  onChange={() => toggleYesNoValue('no', setIsSglFilter)}
                />
                No
              </label>
            </div>
            <div className={styles.filterGroup}>
              <div className={styles.filterGroupTitle}>
                SGM (Small Group Member)
              </div>
              <label className={styles.filterCheckLabel}>
                <input
                  type="checkbox"
                  checked={isSgmFilter.includes('yes')}
                  onChange={() => toggleYesNoValue('yes', setIsSgmFilter)}
                />
                Yes
              </label>
              <label className={styles.filterCheckLabel}>
                <input
                  type="checkbox"
                  checked={isSgmFilter.includes('no')}
                  onChange={() => toggleYesNoValue('no', setIsSgmFilter)}
                />
                No
              </label>
            </div>
            <div className={styles.filterGroup}>
              <div className={styles.filterGroupTitle}>
                Willing to work in TUPAD
              </div>
              <label className={styles.filterCheckLabel}>
                <input
                  type="checkbox"
                  checked={willingFilter.includes('yes')}
                  onChange={() => toggleYesNoValue('yes', setWillingFilter)}
                />
                Yes
              </label>
              <label className={styles.filterCheckLabel}>
                <input
                  type="checkbox"
                  checked={willingFilter.includes('no')}
                  onChange={() => toggleYesNoValue('no', setWillingFilter)}
                />
                No
              </label>
            </div>
            <div className={styles.filterActions}>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsSglFilter([]);
                  setIsSgmFilter([]);
                  setWillingFilter([]);
                }}
              >
                Clear filters
              </Button>
            </div>
          </div>
        )}

        {showColumnFilters && (
          <div className={styles.columnFilters}>
            {OPTIONAL_COLUMNS.map((column) => (
              <label key={column.id} className={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={columnVisibility[column.id]}
                  onChange={() => toggleColumn(column.id)}
                  className={styles.filterCheckbox}
                />
                {column.label}
              </label>
            ))}
          </div>
        )}

        {error && visibleMemberships.length === 0 && (
          <div className={styles.errorState}>
            <p>{error}</p>
            <Button onClick={() => void loadData()}>Retry</Button>
          </div>
        )}

        {loading ? (
          <div className={styles.loadingState}>
            <p>Loading memberships...</p>
          </div>
        ) : sortedMemberships.length > 0 ? (
          <>
            <div className={styles.tableWrap}>
              <div className={styles.tableContainer}>
                {reloading && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}>Loading...</div>
                  </div>
                )}
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {showActionButtons && (
                        <th className={styles.actionsHeader}>Actions</th>
                      )}
                      {visibleColumns.map((column) => (
                        <th
                          key={column.id}
                          onClick={() => handleHeaderSortClick(column.id)}
                          style={{
                            ...(getColumnCellStyle(
                              column.id,
                              compactReadOnlyView,
                            ) ?? {}),
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                          title={`Sort by ${column.label.toLowerCase()}`}
                        >
                          {column.label}
                          {column.id === sortColumnId
                            ? sortDirection === 'asc'
                              ? ' ▲'
                              : ' ▼'
                            : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMemberships.map((membership) => (
                      <tr key={membership.id}>
                        {showActionButtons && (
                          <td className={styles.actionsCell}>
                            <Button
                              size="sm"
                              iconOnly
                              variant="outline"
                              className={styles.historyButton}
                              onClick={() => navigateToHistory(membership.id)}
                              aria-label="View history"
                              title="History"
                            >
                              <History size={14} />
                            </Button>
                            <Button
                              size="sm"
                              iconOnly
                              variant="outline"
                              className={styles.editButton}
                              onClick={() => navigateToEdit(membership.id)}
                              aria-label="Edit membership"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              size="sm"
                              iconOnly
                              variant="danger"
                              className={styles.voidButton}
                              onClick={() => setMembershipToVoid(membership)}
                              aria-label="Delete membership"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        )}
                        {visibleColumns.map((column) => (
                          <td
                            key={`${membership.id}-${column.id}`}
                            style={getColumnCellStyle(
                              column.id,
                              compactReadOnlyView,
                            )}
                          >
                            {renderCellValue(membership, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sortedMemberships.length > tablePageSize && (
                <div className={styles.paginationRow}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTablePage((previous) => previous - 1)}
                    disabled={tablePage <= 1}
                  >
                    Previous
                  </Button>
                  <span className={styles.paginationMeta}>
                    Page {tablePage} of {tableTotalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTablePage((previous) => previous + 1)}
                    disabled={tablePage >= tableTotalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : visibleMemberships.length > 0 ? (
          <div className={styles.emptyState}>
            <p>No members matched your search.</p>
          </div>
        ) : !error ? (
          <div className={styles.emptyState}>
            <p>
              {departmentMinistryFilter?.trim()
                ? 'No members assigned to this department yet.'
                : 'No active memberships found.'}
            </p>
          </div>
        ) : null}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <VoidMembershipModal
          show={membershipToVoid != null}
          onHide={() => setMembershipToVoid(null)}
          membership={membershipToVoid}
          onSuccess={handleVoidSuccess}
          onError={(message) => showToast(message, 'error')}
        />

        <Modal
          show={addressModalData != null}
          onHide={() => setAddressModalData(null)}
          className={styles.modal}
          contentClassName={styles.modalContent}
          dialogClassName={styles.modalDialog}
        >
          <Modal.Header closeButton className={styles.modalHeader}>
            <Modal.Title className={styles.modalTitle}>Address</Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.modalBody}>
            <div className={styles.addressModalGrid}>
              <div className={styles.addressModalColumn}>
                <div className={styles.addressModalLabel}>Name</div>
                <div className={styles.addressModalValue}>
                  {addressModalData?.name}
                </div>
              </div>
              <div className={styles.addressModalColumn}>
                <div className={styles.addressModalLabel}>Address</div>
                <div className={styles.addressModalValue}>
                  {addressModalData?.address}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button variant="outline" onClick={() => setAddressModalData(null)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  },
);

export default Membership;
